// Server-sent events over fetch.
//
// EventSource is not an option: it cannot set an Authorization header, and this
// backend authenticates with a Bearer token. So we read the response body as a
// stream and parse the (very small) SSE grammar ourselves.
//
// Mirrors client.js's error contract so callers can treat both the same way.
import { API_URL, ApiError, SIGNED_OUT_EVENT } from "./client";
import { clearToken, getToken } from "./token";

/**
 * Splits a chunk buffer into complete SSE messages. Messages are separated by a
 * blank line; a partial trailing message stays in the buffer for the next read.
 */
const parseMessages = (buffer) => {
  const parts = buffer.split(/\r?\n\r?\n/);
  const rest = parts.pop() ?? "";
  const messages = [];

  for (const block of parts) {
    let event = "message";
    const dataLines = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith(":")) continue; // comment / heartbeat
      const sep = line.indexOf(":");
      const field = sep === -1 ? line : line.slice(0, sep);
      const value = sep === -1 ? "" : line.slice(sep + 1).replace(/^ /, "");
      if (field === "event") event = value;
      else if (field === "data") dataLines.push(value);
    }
    if (dataLines.length) messages.push({ event, data: dataLines.join("\n") });
  }

  return { messages, rest };
};

/**
 * sseFetch(path, { method, body, onEvent, signal })
 *
 * Calls `onEvent(event, data)` for each message, with `data` already
 * JSON-parsed (falling back to the raw string if it isn't JSON). Resolves when
 * the stream ends. Errors follow client.js: ApiError(0, "API_UNREACHABLE") on
 * network failure, 401 clears the token and broadcasts SIGNED_OUT_EVENT.
 *
 * Defaults to POST because the agent endpoints take a brief in the body;
 * `/notifications/stream` is a long-lived GET.
 */
export const sseFetch = async (path, { method = "POST", body, onEvent, signal } = {}) => {
  const token = getToken();
  if (!token) {
    throw new ApiError(401, "NOT_AUTHENTICATED", "Sesión no encontrada.");
  }

  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (networkError) {
    if (networkError?.name === "AbortError") throw networkError;
    throw new ApiError(
      0,
      "API_UNREACHABLE",
      `No se pudo contactar al servidor: ${networkError.message}`,
    );
  }

  if (!response.ok) {
    // Errors raised before the stream opens arrive as an ordinary JSON body.
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    if (response.status === 401) {
      clearToken();
      window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
      throw new ApiError(401, "UNAUTHORIZED", "Sesión expirada o inválida.");
    }
    throw new ApiError(
      response.status,
      payload?.error || "API_ERROR",
      payload?.message || `API ${response.status}`,
      payload?.details,
    );
  }

  if (!response.body) {
    throw new ApiError(0, "API_UNREACHABLE", "El servidor no devolvió un stream.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const { messages, rest } = parseMessages(buffer);
      buffer = rest;
      for (const { event, data } of messages) {
        let parsed = data;
        try {
          parsed = JSON.parse(data);
        } catch {
          /* not JSON — hand back the raw string */
        }
        onEvent?.(event, parsed);
      }
    }
  } finally {
    reader.releaseLock?.();
  }
};
